import { FunctionComponent, useState } from "react";

import { isGUID } from "@bps/common-utils";
import {
  DefaultButton,
  PrimaryButton,
  Stack,
  Text,
  TextField,
  useTheme
} from "@bps/fluent-ui";

import { ServerMigrationFooter } from "./ServerMigrationFooter";

interface ServerMigrationSelectStageProps {
  tenantName: string;
  sourceFieldDeviceId: string;
  initialDestinationFieldDeviceId: string;
  isLoading: boolean;
  onValidate: (destinationFieldDeviceId: string) => void;
  onCancel: () => void;
}

export const ServerMigrationSelectStage: FunctionComponent<ServerMigrationSelectStageProps> = ({
  tenantName,
  sourceFieldDeviceId,
  initialDestinationFieldDeviceId,
  isLoading,
  onValidate,
  onCancel
}) => {
  const theme = useTheme();
  const [destinationFieldDeviceId, setDestinationFieldDeviceId] = useState(
    initialDestinationFieldDeviceId
  );

  const trimmedDestinationId = destinationFieldDeviceId.trim();

  const getDestinationError = () => {
    if (!trimmedDestinationId) return undefined;
    if (!isGUID(trimmedDestinationId))
      return "Field device id must be a valid GUID.";
    if (
      trimmedDestinationId.toLowerCase() === sourceFieldDeviceId.toLowerCase()
    )
      return "The new field device must be different to the current one.";
    return undefined;
  };

  const destinationError = getDestinationError();

  return (
    <>
      <Stack tokens={{ childrenGap: theme.spacing.m }}>
        <Text>
          Migrate <strong>{tenantName}</strong> from its current server to a new
          field device. The new server will become the site authority for this
          tenant.
        </Text>
        <TextField
          label="Current field device id"
          value={sourceFieldDeviceId}
          readOnly
          borderless
        />
        <TextField
          label="New field device id"
          placeholder="00000000-0000-0000-0000-000000000000"
          value={destinationFieldDeviceId}
          onChange={(_, value) => setDestinationFieldDeviceId(value ?? "")}
          errorMessage={destinationError}
          disabled={isLoading}
          required
          autoFocus
        />
      </Stack>
      <ServerMigrationFooter
        loadingText={isLoading ? "Validating..." : undefined}
      >
        <PrimaryButton
          text="Validate"
          onClick={() => onValidate(trimmedDestinationId)}
          disabled={!trimmedDestinationId || !!destinationError || isLoading}
        />
        <DefaultButton text="Cancel" onClick={onCancel} disabled={isLoading} />
      </ServerMigrationFooter>
    </>
  );
};
